"use client"

import { useState, useEffect, useCallback } from "react"
import { SidebarInset, SidebarTrigger } from "@/components/ui/sidebar"
import { Separator } from "@/components/ui/separator"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { MoreHorizontal, UserPlus, Shield, ShieldOff, Ban, RotateCcw, Trash2, Pencil, MailWarning } from "lucide-react"
import { getUserProfile, setUserProfile, getOrganizationsProfile, userApi, orgApi, authApi, ApiError } from "@/lib/api"
import type { UserProfile, OrganisationProfile, OrgMember } from "@/lib/types"

const ORG_TYPES = [
  "Investment Firm / M&A Team",
  "Renewable Energy Developer",
  "Technical Due Diligence Firm",
  "Energy Analyst / Researcher",
]

const TEAM_SIZES = ["1-5", "6-20", "21-50", "51+"]

const RESEND_COOLDOWN = 180

function ProfileTab({ profile }: { profile: UserProfile }) {
  const initial = { first_name: profile.first_name, last_name: profile.last_name }
  const [form, setForm] = useState(initial)
  const [isEditing, setIsEditing] = useState(false)
  const [profileMsg, setProfileMsg] = useState<{ type: "success" | "error"; text: string } | null>(null)
  const [saving, setSaving] = useState(false)

  const [pwForm, setPwForm] = useState({ old_password: "", new_password: "", confirm_password: "" })
  const [pwMsg, setPwMsg] = useState<{ type: "success" | "error"; text: string } | null>(null)
  const [savingPw, setSavingPw] = useState(false)

  const [countdown, setCountdown] = useState(0)
  const [resending, setResending] = useState(false)
  const [resendMsg, setResendMsg] = useState<{ type: "success" | "error"; text: string } | null>(null)

  useEffect(() => {
    if (countdown <= 0) return
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [countdown])

  const handleResend = async () => {
    setResending(true)
    setResendMsg(null)
    try {
      await authApi.resendVerification(profile.email)
      setCountdown(RESEND_COOLDOWN)
      setResendMsg({ type: "success", text: "Verification email sent. Check your inbox." })
    } catch (err) {
      setResendMsg({ type: "error", text: err instanceof ApiError ? err.message : "Failed to send verification email." })
    } finally {
      setResending(false)
    }
  }

  const handleCancel = () => {
    setForm(initial)
    setProfileMsg(null)
    setIsEditing(false)
  }

  const handleProfileSave = async () => {
    setSaving(true)
    setProfileMsg(null)
    try {
      await userApi.updateUser(profile.id, { first_name: form.first_name, last_name: form.last_name })
      const updated = await userApi.getUser(profile.id)
      setUserProfile({ ...profile, ...updated.data })
      setProfileMsg({ type: "success", text: "Profile updated." })
      setIsEditing(false)
    } catch (err) {
      setProfileMsg({ type: "error", text: err instanceof ApiError ? err.message : "Failed to save." })
    } finally {
      setSaving(false)
    }
  }

  const handlePasswordSave = async () => {
    if (pwForm.new_password !== pwForm.confirm_password) {
      setPwMsg({ type: "error", text: "New passwords do not match." })
      return
    }
    setSavingPw(true)
    setPwMsg(null)
    try {
      await userApi.changePassword(profile.id, pwForm)
      setPwForm({ old_password: "", new_password: "", confirm_password: "" })
      setPwMsg({ type: "success", text: "Password changed." })
    } catch (err) {
      setPwMsg({ type: "error", text: err instanceof ApiError ? err.message : "Failed to change password." })
    } finally {
      setSavingPw(false)
    }
  }

  return (
    <div className="space-y-6">
      {!profile.is_email_verified && (
        <div className="border border-secondary bg-secondary/10 p-4">
          <div className="flex items-start gap-3">
            <MailWarning className="h-5 w-5 text-secondary shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-foreground">Email not verified</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                We sent a verification email to <span className="font-medium">{profile.email}</span>. Click the link in the email to verify your account.
              </p>
              {resendMsg && (
                <p className={`text-sm mt-2 ${resendMsg.type === "success" ? "text-primary" : "text-destructive"}`}>
                  {resendMsg.text}
                </p>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={handleResend}
                disabled={resending || countdown > 0}
                className="rounded-none mt-3 border-secondary hover:bg-secondary/20"
              >
                {resending
                  ? "Sending…"
                  : countdown > 0
                    ? `Resend in ${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, "0")}`
                    : "Resend verification email"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle>Personal information</CardTitle>
            <CardDescription>Your name and email address.</CardDescription>
          </div>
          {!isEditing && (
            <Button variant="outline" size="sm" className="rounded-none" onClick={() => setIsEditing(true)}>
              <Pencil className="h-3.5 w-3.5 mr-1.5" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                First name
              </Label>
              <Input
                value={form.first_name}
                onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                disabled={!isEditing}
                className="rounded-none disabled:bg-muted/50"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Last name
              </Label>
              <Input
                value={form.last_name}
                onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                disabled={!isEditing}
                className="rounded-none disabled:bg-muted/50"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Email
            </Label>
            <Input value={profile.email} disabled className="rounded-none bg-muted/50" />
          </div>
          {profileMsg && (
            <p className={`text-sm ${profileMsg.type === "success" ? "text-primary" : "text-destructive"}`}>
              {profileMsg.text}
            </p>
          )}
          {isEditing && (
            <div className="flex gap-2">
              <Button onClick={handleProfileSave} disabled={saving} className="rounded-none bg-primary text-white hover:bg-foreground">
                {saving ? "Saving…" : "Save changes"}
              </Button>
              <Button variant="outline" onClick={handleCancel} disabled={saving} className="rounded-none">
                Cancel
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Change password</CardTitle>
          <CardDescription>Enter your current password and choose a new one.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Current password
            </Label>
            <Input
              type="password"
              value={pwForm.old_password}
              onChange={(e) => setPwForm((f) => ({ ...f, old_password: e.target.value }))}
              className="rounded-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                New password
              </Label>
              <Input
                type="password"
                value={pwForm.new_password}
                onChange={(e) => setPwForm((f) => ({ ...f, new_password: e.target.value }))}
                className="rounded-none"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Confirm new password
              </Label>
              <Input
                type="password"
                value={pwForm.confirm_password}
                onChange={(e) => setPwForm((f) => ({ ...f, confirm_password: e.target.value }))}
                className="rounded-none"
              />
            </div>
          </div>
          {pwMsg && (
            <p className={`text-sm ${pwMsg.type === "success" ? "text-primary" : "text-destructive"}`}>
              {pwMsg.text}
            </p>
          )}
          <Button
            onClick={handlePasswordSave}
            disabled={savingPw}
            className="rounded-none bg-primary text-white hover:bg-foreground"
          >
            {savingPw ? "Saving…" : "Change password"}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

function OrganisationTab({ orgId, storedOrg }: { orgId: string; storedOrg: OrganisationProfile | null }) {
  const fallback = storedOrg
    ? { name: storedOrg.name, type: storedOrg.type, team_size: storedOrg.team_size, country: storedOrg.country }
    : { name: "", type: "", team_size: "", country: "" }

  const [form, setForm] = useState(fallback)
  const [isEditing, setIsEditing] = useState(false)
  const [msg, setMsg] = useState<{ type: "success" | "error"; text: string } | null>(null)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(!storedOrg)

  useEffect(() => {
    orgApi.getOrganisation(orgId)
      .then((res) => {
        setForm({
          name: res.data.name,
          type: res.data.type,
          team_size: res.data.team_size,
          country: res.data.country,
        })
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [orgId])

  const handleCancel = () => {
    setMsg(null)
    setIsEditing(false)
  }

  const handleSave = async () => {
    setSaving(true)
    setMsg(null)
    try {
      await orgApi.updateOrganisation(orgId, form)
      setMsg({ type: "success", text: "Organisation updated." })
      setIsEditing(false)
    } catch (err) {
      setMsg({ type: "error", text: err instanceof ApiError ? err.message : "Failed to save." })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="h-40 rounded-lg bg-muted animate-pulse" />
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle>Organisation details</CardTitle>
          <CardDescription>Manage your organisation's profile information.</CardDescription>
        </div>
        {!isEditing && (
          <Button variant="outline" size="sm" className="rounded-none" onClick={() => setIsEditing(true)}>
            <Pencil className="h-3.5 w-3.5 mr-1.5" />
            Edit
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Organisation name
          </Label>
          <Input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            disabled={!isEditing}
            className="rounded-none disabled:bg-muted/50"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Type
            </Label>
            <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))} disabled={!isEditing}>
              <SelectTrigger className="rounded-none disabled:bg-muted/50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ORG_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Team size
            </Label>
            <Select value={form.team_size} onValueChange={(v) => setForm((f) => ({ ...f, team_size: v }))} disabled={!isEditing}>
              <SelectTrigger className="rounded-none disabled:bg-muted/50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TEAM_SIZES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Country
          </Label>
          <Input
            value={form.country}
            onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
            disabled={!isEditing}
            className="rounded-none disabled:bg-muted/50"
          />
        </div>
        {msg && (
          <p className={`text-sm ${msg.type === "success" ? "text-primary" : "text-destructive"}`}>
            {msg.text}
          </p>
        )}
        {isEditing && (
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving} className="rounded-none bg-primary text-white hover:bg-foreground">
              {saving ? "Saving…" : "Save changes"}
            </Button>
            <Button variant="outline" onClick={handleCancel} disabled={saving} className="rounded-none">
              Cancel
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function TeamMembersTab({ currentUserId }: { currentUserId: string }) {
  const [members, setMembers] = useState<OrgMember[]>([])
  const [loading, setLoading] = useState(true)
  const [inviteEmail, setInviteEmail] = useState("")
  const [inviteMsg, setInviteMsg] = useState<{ type: "success" | "error"; text: string } | null>(null)
  const [inviting, setInviting] = useState(false)
  const [actionMsg, setActionMsg] = useState<{ type: "success" | "error"; text: string } | null>(null)
  const [confirmRemove, setConfirmRemove] = useState<OrgMember | null>(null)
  const [confirmRevoke, setConfirmRevoke] = useState<OrgMember | null>(null)

  const loadMembers = useCallback(() => {
    setLoading(true)
    userApi.getMembers()
      .then((res) => setMembers(res.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { loadMembers() }, [loadMembers])

  const handleInvite = async () => {
    if (!inviteEmail) return
    setInviting(true)
    setInviteMsg(null)
    try {
      await userApi.inviteMember(inviteEmail)
      setInviteEmail("")
      setInviteMsg({ type: "success", text: `Invite sent to ${inviteEmail}.` })
    } catch (err) {
      setInviteMsg({ type: "error", text: err instanceof ApiError ? err.message : "Failed to send invite." })
    } finally {
      setInviting(false)
    }
  }

  const doAction = async (action: () => Promise<unknown>, successMsg: string) => {
    setActionMsg(null)
    try {
      await action()
      setActionMsg({ type: "success", text: successMsg })
      loadMembers()
    } catch (err) {
      setActionMsg({ type: "error", text: err instanceof ApiError ? err.message : "Action failed." })
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Invite a member</CardTitle>
          <CardDescription>Send an invite link to a new team member.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="colleague@company.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="rounded-none flex-1"
            />
            <Button
              onClick={handleInvite}
              disabled={inviting || !inviteEmail}
              className="rounded-none bg-primary text-white hover:bg-foreground"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              {inviting ? "Sending…" : "Send invite"}
            </Button>
          </div>
          {inviteMsg && (
            <p className={`text-sm mt-2 ${inviteMsg.type === "success" ? "text-primary" : "text-destructive"}`}>
              {inviteMsg.text}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Team members</CardTitle>
          <CardDescription>Manage roles and access for your organisation.</CardDescription>
        </CardHeader>
        <CardContent>
          {actionMsg && (
            <p className={`text-sm mb-4 ${actionMsg.type === "success" ? "text-primary" : "text-destructive"}`}>
              {actionMsg.text}
            </p>
          )}
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 rounded-lg bg-muted animate-pulse" />
              ))}
            </div>
          ) : members.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No members found.</p>
          ) : (
            <div className="space-y-2">
              {members.map((member) => {
                const isCurrentUser = member.user.id === currentUserId
                const initials = `${member.user.first_name?.[0] ?? ""}${member.user.last_name?.[0] ?? ""}`
                const isAdmin = member.role === "org_admin"
                const isSuspended = member.status === "suspended"

                return (
                  <div
                    key={member.id}
                    className="flex items-center gap-3 p-3 rounded-lg border border-border"
                  >
                    <Avatar className="h-9 w-9 rounded-lrink-0">
                      <AvatarImage src={member.user.photo_url ?? ""} />
                      <AvatarFallback className="rounded-lg text-xs">{initials}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">
                          {member.user.first_name} {member.user.last_name}
                          {isCurrentUser && <span className="text-muted-foreground font-normal"> (you)</span>}
                        </span>
                        <Badge
                          variant={isAdmin ? "default" : "secondary"}
                          className="text-xs shrink-0"
                        >
                          {isAdmin ? "Admin" : "Member"}
                        </Badge>
                        {isSuspended && (
                          <Badge variant="destructive" className="text-xs shrink-0">
                            Suspended
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{member.user.email}</p>
                    </div>
                    {!isCurrentUser && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {!isAdmin && (
                            <DropdownMenuItem
                              onClick={() => doAction(
                                () => userApi.makeAdmin(member.user.id),
                                `${member.user.first_name} promoted to admin.`
                              )}
                            >
                              <Shield className="h-4 w-4 mr-2" />
                              Make admin
                            </DropdownMenuItem>
                          )}
                          {isAdmin && (
                            <DropdownMenuItem
                              onClick={() => setConfirmRevoke(member)}
                            >
                              <ShieldOff className="h-4 w-4 mr-2" />
                              Revoke admin
                            </DropdownMenuItem>
                          )}
                          {!isSuspended ? (
                            <DropdownMenuItem
                              onClick={() => doAction(
                                () => userApi.suspendMember(member.user.id),
                                `${member.user.first_name} suspended.`
                              )}
                            >
                              <Ban className="h-4 w-4 mr-2" />
                              Suspend
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => doAction(
                                () => userApi.restoreMember(member.user.id),
                                `${member.user.first_name} restored.`
                              )}
                            >
                              <RotateCcw className="h-4 w-4 mr-2" />
                              Restore
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => setConfirmRemove(member)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!confirmRemove} onOpenChange={(open) => !open && setConfirmRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove member</AlertDialogTitle>
            <AlertDialogDescription>
              {`Are you sure you want to remove ${confirmRemove?.user.first_name} ${confirmRemove?.user.last_name} from the organisation? This cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => {
                if (confirmRemove) {
                  doAction(
                    () => userApi.removeMember(confirmRemove.user.id),
                    `${confirmRemove.user.first_name} removed.`
                  )
                  setConfirmRemove(null)
                }
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmRevoke} onOpenChange={(open) => !open && setConfirmRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke admin access</AlertDialogTitle>
            <AlertDialogDescription>
              {`Are you sure you want to revoke admin access for ${confirmRevoke?.user.first_name} ${confirmRevoke?.user.last_name}? They will remain a member of the organisation.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmRevoke) {
                  doAction(
                    () => userApi.revokeAdmin(confirmRevoke.user.id),
                    `${confirmRevoke.user.first_name}'s admin access revoked.`
                  )
                  setConfirmRevoke(null)
                }
              }}
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default function SettingsPage() {
  const profile = getUserProfile()
  const isAdmin = profile?.role === "org_admin"
  const storedOrgs = getOrganizationsProfile()
  const orgId = profile?.organization_id ?? storedOrgs?.[0]?.id
  const storedOrg = storedOrgs?.[0] ?? null

  if (!profile) return null

  return (
    <SidebarInset>
      <header className="flex h-16 shrink-0 items-center gap-2 border-b border-border px-4 bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60">
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mr-2 h-4" />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem className="hidden md:block">
              <BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="hidden md:block" />
            <BreadcrumbItem>
              <BreadcrumbPage>Settings</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </header>

      <div className="flex flex-1 flex-col gap-6 p-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Settings</h1>
          <p className="text-muted-foreground mt-1">Manage your profile and organisation.</p>
        </div>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="rounded-none border-b border-border bg-transparent p-0 h-auto">
            <TabsTrigger
              value="profile"
              className="rounded-none border-b-2 border-transparent px-4 pb-3 pt-0 data-[state=active]:border-b-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none font-medium"
            >
              My Profile
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger
                value="organisation"
                className="rounded-none border-b-2 border-transparent px-4 pb-3 pt-0 data-[state=active]:border-b-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none font-medium"
              >
                Organisation
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger
                value="team"
                className="rounded-none border-b-2 border-transparent px-4 pb-3 pt-0 data-[state=active]:border-b-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none font-medium"
              >
                Team Members
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="profile" className="mt-0">
            <ProfileTab profile={profile} />
          </TabsContent>

          {isAdmin && orgId && (
            <TabsContent value="organisation" className="mt-0">
              <OrganisationTab orgId={orgId} storedOrg={storedOrg} />
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="team" className="mt-0">
              <TeamMembersTab currentUserId={profile.id} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </SidebarInset>
  )
}
